import imageCompression from 'browser-image-compression';
import type { UploadFile } from '@/types/upload-file.model';
import ApiClient from './axios/apiClient';
import type { Media } from '@/types/media';

/**
 * Uploads a file to the server, compressing it if it is a large image.
 * @param file - The file to upload
 * @returns Promise containing the uploaded file information
 */
export const uploadFile = async (
  file: File, 
): Promise<Media[]> => {
  try {
    let fileToUpload = file;
    const isImage = file.type.startsWith('image/');
    const isLarge = file.size / 1024 > 200; // size in KB

    if (isImage && isLarge) {
      console.log(`Image is large (${(file.size / 1024).toFixed(2)} KB), attempting to compress...`);
      
      const options = {
        maxSizeMB: 0.2, // Target size of 200KB
        maxWidthOrHeight: 1920, // Resize to a max of 1920px on the largest side
        useWebWorker: true,
      };

      try {
        const compressedFile = await imageCompression(file, options);
        console.log(`Image compressed successfully: ${(compressedFile.size / 1024).toFixed(2)} KB`);
        fileToUpload = compressedFile;
      } catch (error) {
        console.error('Error during image compression, uploading original file:', error);
        // Fallback to uploading the original file if compression fails
      }
    }

    const formData = new FormData();
    formData.append("files", fileToUpload);

    const response = await ApiClient.post<Media[]>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Uploads multiple files to the server
 * @param files - Array of files to upload
 * @param fieldName - The field name for the files in the form data (optional, defaults to 'files')
 * @returns Promise containing array of uploaded file information
 */
export const uploadMultipleFiles = async (
  files: File[], 
  fieldName: string = 'files'
): Promise<UploadFile[]> => {
  try {
    const formData = new FormData();
    
    files.forEach((file) => {
      formData.append(fieldName, file);
    });

    const response = await ApiClient.post<UploadFile[]>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

/**
 * Gets file information by ID
 * @param fileId - The ID of the file to retrieve
 * @returns Promise containing the file information
 */
export const getFileById = async (fileId: number): Promise<UploadFile> => {
  try {
    const response = await ApiClient.get<UploadFile>(`/upload/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching file:', error);
    throw error;
  }
};

/**
 * Gets all files
 * @returns Promise containing list of files
 */
export const getAllFiles = async (): Promise<UploadFile[]> => {
  try {
    const response = await ApiClient.get<UploadFile[]>('/upload/files');
    return response.data;
  } catch (error) {
    console.error('Error fetching files:', error);
    throw error;
  }
};

/**
 * Deletes a file by ID
 * @param fileId - The ID of the file to delete
 * @returns Promise that resolves when file is deleted
 */
export const deleteFile = async (fileId: number): Promise<void> => {
  try {
    await ApiClient.delete(`/upload/files/${fileId}`);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};